import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarRange, Users, KeyRound, ScanLine, MailWarning, ChevronRight, FileText, Wallet, FileSignature, Receipt, Download, Clock, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { PortalCommercial } from "@/components/public/PortalCommercial";
import { PortalRequests } from "@/components/public/PortalRequests";

const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fshort = (s) => {
  if (!s) return "—";
  try { return new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }); } catch { return s; }
};
const fdate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const today = () => new Date().toISOString().slice(0, 10);

const badge = (b) => {
  if (b.status === "cancelled") return { label: "annulée", cls: "bg-[#868E96] text-white" };
  if (b.status === "arrived") return { label: "sur place", cls: "bg-[#FFD700] text-black" };
  if (b.payment_status === "paid") return { label: "payée en ligne", cls: "bg-[#059669] text-white" };
  if (b.payment_status === "paid_onsite") return { label: "payée sur place", cls: "bg-[#059669] text-white" };
  return { label: "à régler sur place", cls: "bg-[#002FA7] text-white" };
};
const PAY_STATE = { paid: ["Réglé", "bg-[#059669] text-white"], partial: ["Acompte versé", "bg-[#FFD700] text-black"], due: ["À régler", "bg-[#002FA7] text-white"], cancelled: ["Annulée", "bg-[#868E96] text-white"] };

const Card = ({ b, onOpen }) => {
  const st = badge(b);
  return (
    <div className="border border-white/15 bg-white/5 p-4 grid sm:grid-cols-[110px_1fr_auto] gap-4 items-center" data-testid={`cb-item-${b.reference}`}>
      <img src={`${API}/public/bookings/${b.qr_token}/qr.png`} alt={`QR ${b.reference}`} className="w-[100px] h-[100px] object-contain bg-white p-1" data-testid={`cb-qr-${b.reference}`} />
      <div>
        <div className="font-display font-black text-xl tracking-tight leading-none">{b.unit_label}</div>
        <div className="text-sm text-white/60 flex items-center gap-2 mt-1"><CalendarRange className="h-4 w-4" />{fshort(b.date_from)} → {fshort(b.date_to)}<span className="text-white/40">·</span><Users className="h-4 w-4" />{b.guests}</div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 ${st.cls}`}>{st.label}</span>
          <span className="font-mono text-[11px] tracking-[0.15em] flex items-center gap-1 text-white/70"><KeyRound className="h-3 w-3" />{b.reference}</span>
          <span className="text-[11px] text-white/50">{eur(b.total)}</span>
        </div>
      </div>
      <Button variant="outline" onClick={onOpen} data-testid={`cb-open-${b.reference}`} className="border-white/25 text-white hover:bg-white hover:text-black">Voir le billet<ChevronRight className="h-4 w-4 ml-1" /></Button>
    </div>
  );
};

function Reservations({ data, code, navigate }) {
  const t = today();
  const upcoming = data.bookings.filter((b) => (b.date_to || "") >= t);
  const past = data.bookings.filter((b) => (b.date_to || "") < t);
  const open = (b) => navigate(`/sites/${data.entreprise?.code || code}/reservation/${b.qr_token}`);
  return (
    <div data-testid="cp-panel-reservations">
      <div className="border border-white/15 bg-white/5 p-3 text-[11px] text-white/60 flex items-start gap-2 mb-6" data-testid="cb-help">
        <ScanLine className="h-4 w-4 text-[#FFD700] shrink-0 mt-0.5" />Le QR code est lu par le système PAG de l'établissement. Si le scan échoue, le code de confirmation affiché sous chaque réservation suffit à la réception.
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2">À venir</div>
      {upcoming.length === 0 ? (
        <div className="border border-white/15 bg-white/5 p-6 text-sm text-white/60" data-testid="cb-empty-upcoming">Aucun séjour à venir.
          <Button variant="outline" size="sm" data-testid="cb-book-again" className="ml-3 border-white/25 text-white hover:bg-white hover:text-black" onClick={() => navigate(`/sites/${data.entreprise?.code || code}/reservation`)}>Réserver</Button>
        </div>
      ) : <div className="space-y-3" data-testid="cb-upcoming">{upcoming.map((b) => <Card key={b.id} b={b} onOpen={() => open(b)} />)}</div>}
      {past.length > 0 && (
        <div className="mt-8"><div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2">Séjours passés</div>
          <div className="space-y-3 opacity-60" data-testid="cb-past">{past.map((b) => <Card key={b.id} b={b} onOpen={() => open(b)} />)}</div></div>
      )}
    </div>
  );
}

function Documents({ data, token }) {
  if (!data.documents.length) return <Empty icon={FileText} testid="cp-docs-empty" text={`Aucun document partagé pour le moment. ${data.entreprise?.name || "L'établissement"} peut déposer ici vos contrats, reçus et informations pratiques.`} />;
  return (
    <div className="space-y-3" data-testid="cp-panel-documents">
      {data.documents.map((d) => (
        <div key={d.id} className="border border-white/15 bg-white/5 p-4 flex items-center gap-4" data-testid={`cp-doc-${d.id}`}>
          <div className="h-11 w-11 bg-white/10 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-[#FFD700]" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{d.title} {!d.downloaded_at && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#FFD700] text-black ml-1" data-testid={`cp-doc-new-${d.id}`}>nouveau</span>}</div>
            <div className="text-[11px] text-white/50">{d.category_label} · {fdate(d.created_at)} · {(d.size / 1024).toFixed(0)} Ko</div>
          </div>
          <a href={`${API}/public/client/${token}/documents/${d.id}`} target="_blank" rel="noreferrer" data-testid={`cp-doc-download-${d.id}`} className="inline-flex items-center gap-1 border border-white/25 px-3 py-2 text-xs font-semibold hover:bg-white hover:text-black transition-colors"><Download className="h-3.5 w-3.5" /> Ouvrir</a>
        </div>
      ))}
    </div>
  );
}

function Payments({ data }) {
  const c = data.counts;
  return (
    <div data-testid="cp-panel-payments">
      <div className="grid grid-cols-3 gap-0 border border-white/20 mb-5">
        <Big label="Réglé" value={eur(c.total_paid)} testid="cp-pay-paid" />
        <Big label="Reste à régler" value={eur(c.total_due)} testid="cp-pay-due" highlight={c.total_due > 0} />
        <Big label="Échéances" value={c.payments_due} sub="séjours à régler" testid="cp-pay-count" />
      </div>
      {data.payments.length === 0 ? <Empty icon={Wallet} testid="cp-pay-empty" text="Aucun paiement." /> : (
        <div className="border border-white/15 bg-white/5 divide-y divide-white/10">
          {data.payments.map((p) => {
            const [label, cls] = PAY_STATE[p.state] || PAY_STATE.due;
            return (
              <div key={p.booking_id} className="p-4 grid sm:grid-cols-[1fr_auto_auto] gap-3 items-center" data-testid={`cp-pay-${p.reference}`}>
                <div><div className="font-semibold">{p.label} <span className="font-mono text-[11px] text-white/50 ml-1">{p.reference}</span></div>
                  <div className="text-[11px] text-white/50 flex items-center gap-1"><Clock className="h-3 w-3" />{fshort(p.date_from)} → {fshort(p.date_to)} · {p.payment_mode === "paypal" ? "PayPal" : "sur place"}{p.paid_at ? ` · réglé le ${fdate(p.paid_at)}` : ""}</div></div>
                <div className="text-right"><div className="font-mono font-bold">{eur(p.total)}</div>{p.state === "partial" && <div className="text-[11px] text-white/50">acompte {eur(p.paid)} · reste {eur(p.remaining)}</div>}</div>
                <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 ${cls}`}>{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Soon({ icon: Icon, block, testid }) {
  return <Empty icon={Icon} testid={testid} text={block.message} soon />;
}
export { Empty };

function Empty({ icon: Icon, text, testid, soon }) {
  return (
    <div className="border border-dashed border-white/25 bg-white/5 p-8 text-center" data-testid={testid}>
      <Icon className="h-8 w-8 mx-auto text-[#FFD700] mb-3" />
      {soon && <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFD700] mb-1">Bientôt disponible</div>}
      <div className="text-sm text-white/60 max-w-md mx-auto">{text}</div>
    </div>
  );
}

function Big({ label, value, sub, highlight, testid }) {
  return (
    <div data-testid={testid} className={`p-4 border-r border-white/20 last:border-r-0 ${highlight ? "bg-[#FFD700] text-black" : ""}`}>
      <div className={`text-[10px] uppercase tracking-[0.25em] font-bold ${highlight ? "text-black/70" : "text-white/50"}`}>{label}</div>
      <div className="font-display text-xl sm:text-2xl font-black tracking-tighter mt-1 break-words">{value}</div>
      {sub && <div className={`text-[10px] ${highlight ? "text-black/60" : "text-white/40"}`}>{sub}</div>}
    </div>
  );
}

const TABS = [
  { key: "reservations", label: "Réservations", icon: CalendarRange, count: (c) => c.bookings },
  { key: "demandes", label: "Mes demandes", icon: MessagesSquare, count: (c, d) => d.requests?.count || 0, dot: (c, d) => (d.requests?.count || 0) > 0 },
  { key: "documents", label: "Documents", icon: FileText, count: (c) => c.documents, dot: (c) => c.documents_new > 0 },
  { key: "paiements", label: "Paiements", icon: Wallet, count: (c) => c.payments_due, dot: (c) => c.payments_due > 0 },
  { key: "devis", label: "Devis", icon: FileSignature, soon: (d) => !d.devis?.available, count: (c) => c.devis || 0, dot: (c) => (c.devis_pending || 0) > 0 },
  { key: "factures", label: "Factures", icon: Receipt, soon: (d) => !d.factures?.available, count: (c) => c.factures || 0, dot: (c) => (c.factures_due || 0) > 0 },
];
const TAB_KEYS = TABS.map((t) => t.key);

export default function PublicClientBookings() {
  const { code, token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return TAB_KEYS.includes(t) ? t : "reservations"; });

  const load = () => api.get(`/public/client/${token}/portal`).then((r) => setData(r.data)).catch((e) => setData({ error: formatApiError(e.response?.data?.detail) }));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Retour de Stripe Checkout : on confirme le paiement côté serveur (polling court) puis on affiche la facture payée
    const params = new URLSearchParams(window.location.search);
    const did = params.get("paid_doc"), sid = params.get("session_id");
    if (params.get("pay_cancelled")) { toast("Paiement annulé — votre facture reste à régler."); setTab("factures"); window.history.replaceState({}, "", window.location.pathname); return; }
    if (!did || !sid) return;
    setTab("factures");
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data: r } = await api.get(`/public/client/${token}/commercial/${did}/pay/status?session_id=${encodeURIComponent(sid)}`);
        if (r.payment_status === "paid") { toast.success(`Paiement reçu — facture ${r.document.number} réglée. Merci !`); load(); window.history.replaceState({}, "", window.location.pathname); return; }
      } catch { /* on réessaie */ }
      if (tries < 8) setTimeout(poll, 2000); else toast("Paiement en cours de confirmation — actualisez dans quelques instants.");
    };
    poll();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (doc, decision, reason) => {
    try { await api.post(`/public/client/${token}/commercial/${doc.id}/${decision}`, decision === "decline" ? { reason } : {}); toast.success(decision === "accept" ? `Devis ${doc.number} accepté — merci !` : `Devis ${doc.number} refusé`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (data?.error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="cb-expired">
        <div className="max-w-md text-center">
          <MailWarning className="h-10 w-10 mx-auto text-[#FFD700] mb-3" />
          <div className="font-display font-black text-2xl tracking-tight">Lien indisponible</div>
          <div className="text-sm text-white/60 mt-2">{data.error}</div>
          <Button variant="outline" className="mt-4 border-white/25 text-white hover:bg-white hover:text-black" onClick={() => navigate(`/sites/${code}/reservation`)} data-testid="cb-new-link">Demander un nouveau lien</Button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-white/50 p-10" data-testid="cb-loading">Chargement…</div>;

  const c = data.counts;
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="client-bookings-page">
      <div className="max-w-4xl mx-auto p-5 lg:p-10">
        <button onClick={() => navigate(`/sites/${data.entreprise?.code || code}`)} data-testid="cb-back-link" className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white flex items-center gap-1 mb-4"><ArrowLeft className="h-3 w-3" />{data.entreprise?.name}</button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700] font-bold mb-2">// Espace client</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Mon espace client</h1>
        <p className="text-base text-white/60 mt-2" data-testid="cb-count">
          {c.bookings} réservation{c.bookings > 1 ? "s" : ""}{data.requests?.count ? ` · ${data.requests.count} demande${data.requests.count > 1 ? "s" : ""}` : ""} liée{c.bookings + (data.requests?.count || 0) > 1 ? "s" : ""} à {data.email}. Chaque QR code vaut preuve de réservation à la réception.
          {data.expires_at && <span className="block text-[11px] text-white/40 mt-1" data-testid="cp-expiry">Accès valable jusqu'au {fdate(data.expires_at)} · sans mot de passe</span>}
        </p>

        <div className="mt-7 flex flex-wrap gap-1 border-b border-white/15" data-testid="cp-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cp-tab-${t.key}`} aria-selected={active}
                className={`relative flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${active ? "border-[#FFD700] text-white" : "border-transparent text-white/50 hover:text-white"}`}>
                <t.icon className="h-4 w-4" />{t.label}
                {(typeof t.soon === "function" ? t.soon(data) : t.soon) ? <span className="text-[8px] px-1 py-0.5 bg-white/10 text-white/60">bientôt</span> : <span className="text-[10px] font-mono text-white/50">{t.count(c, data)}</span>}
                {t.dot && t.dot(c, data) && <span className="absolute top-2 right-2 h-1.5 w-1.5 bg-[#FFD700]" />}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {tab === "reservations" && <Reservations data={data} code={code} navigate={navigate} />}
          {tab === "demandes" && <PortalRequests token={token} entrepriseName={data.entreprise?.name} onCountChanged={(cnt) => setData((d) => (d && d.requests?.count !== cnt.total ? { ...d, requests: { ...(d.requests || {}), count: cnt.total } } : d))} />}
          {tab === "documents" && <Documents data={data} token={token} />}
          {tab === "paiements" && <Payments data={data} />}
          {tab === "devis" && (data.devis.available ? <PortalCommercial kind="devis" block={data.devis} token={token} onDecide={decide} /> : <Soon icon={FileSignature} block={data.devis} testid="cp-panel-devis" />)}
          {tab === "factures" && (data.factures.available ? <PortalCommercial kind="factures" block={data.factures} token={token} /> : <Soon icon={Receipt} block={data.factures} testid="cp-panel-factures" />)}
        </div>

        {(data.entreprise?.telephone || data.entreprise?.email) && (
          <div className="mt-10 pt-6 border-t border-white/10 text-[11px] text-white/50" data-testid="cp-contact">
            Une question ? {data.entreprise.name} · {data.entreprise.telephone && <a href={`tel:${data.entreprise.telephone}`} className="hover:text-white">{data.entreprise.telephone}</a>} {data.entreprise.email && <> · <a href={`mailto:${data.entreprise.email}`} className="hover:text-white">{data.entreprise.email}</a></>}
          </div>
        )}
      </div>
    </div>
  );
}
